import { WheelEvent } from "react";
import { Controller, RegisterOptions, useFormContext } from "react-hook-form";
import type { FormFieldPath, FormValues } from "types/form.types";
import { FormField } from "./FormField";

type FormFieldControlledProps<TFieldName extends FormFieldPath = FormFieldPath> = {
  labelTitle: string;
  fieldName: TFieldName;
  className?: string;
  type?: "text" | "number" | "email";
  placeholder?: string;
  multiline?: boolean;
  rules?: RegisterOptions<FormValues, TFieldName>;
  defaultValue?: string;
  disabled?: boolean;
};

export const FormFieldControlled = <TFieldName extends FormFieldPath>({
  labelTitle,
  fieldName,
  className,
  multiline,
  type = "number",
  placeholder = type === "number" ? "0" : "",
  rules,
  defaultValue,
  disabled = false,
}: FormFieldControlledProps<TFieldName>) => {
  const { control } = useFormContext<FormValues>();
  const isNumber = type === "number";

  return (
    <Controller<FormValues, TFieldName>
      shouldUnregister
      control={control}
      name={fieldName}
      rules={rules}
      render={({ field: { name, value, onChange, onBlur }, fieldState: { error } }) => (
        <FormField
          name={name}
          labelTitle={labelTitle}
          type={type}
          placeholder={placeholder}
          multiline={multiline}
          className={className}
          value={isNumber ? String(value ?? "") : String(value ?? defaultValue ?? "")}
          onChange={(rawValue) => {
            onChange(isNumber ? (rawValue ? parseInt(rawValue, 10) : "") : rawValue);
          }}
          onBlur={() => {
            onBlur();
          }}
          onWheel={isNumber ? (e: WheelEvent<HTMLInputElement>) => e.currentTarget.blur() : undefined}
          error={error}
          disabled={disabled}
        />
      )}
    />
  );
};
